import {
    all,
    call,
    cancel,
    delay,
    fork,
    join,
    put, 
    race,
    take
} from "redux-saga/effects";
import { createSelector } from "reselect";
import * as R from "ramda";

import { ONE_SECOND } from "Globals/constants/constants";
import { PAGEVISIBILITY } from "Globals/global.duck";

export const sagaTestHelper = generator => {
    let input;
    return (title, fn) => {
        it(title, (...rest) => {
            if (input instanceof Error) {
                const result = generator.throw(input);
                input = fn(result.value, ...rest);
            } else {
                const result = generator.next(input);
                input = fn(result.value, ...rest);
            }
        });
    };
};

export const getId = (action, defaultId = "global") => {
    const { meta: { scopeID = defaultId } = { scopeID: defaultId } } = action || {};
    return scopeID;
};

export const getContext = action => {
    const { meta: { mdContext } = { } } = action || {};
    return mdContext;
};

const isSagaAction = Symbol("IS_SAGA_ACTION");
export const bindActionCreators = (iid, acs, gacs = {}, opts = {}) => {
    return () => createSelector(
        R.identity,
        R.always(acs),
        R.always(gacs),
        (_, ownProps) => iid || ownProps.id,
        (_, ownProps) => ownProps?.mdContext || {},
        (dispatch, actionCreators, globalActionCreators, id, mdContext) =>
        {
            return Object.assign(
                {},
                ...Object.entries(actionCreators).map(([ key, fn ]) => ({
                    [key]: (...args) => {
                        const value = { ...fn(...args), meta: { scopeID: id, mdContext } };
                        const sagaValue = dispatch(value);
                        return opts[isSagaAction] ? sagaValue : () => dispatch({ ...value, type: `STOP_${value.type}`, ignoreUnhandled: true });
                    }
                })),
                ...Object.entries(globalActionCreators).map(([ key, fn ]) => ({
                    [key]: (...args) => {
                        const value = { ...fn(...args), meta: { mdContext } };
                        const sagaValue = dispatch(value);
                        return opts[isSagaAction] ? sagaValue : () => dispatch({ ...value, type: `STOP_${value.type}`, ignoreUnhandled: true });
                    }
                }))
            );
        }
    );
};

export const injectBoundActionCreatorsFactory = (
    actions,
    getIdFunc = getId,
    defaultId
) => saga =>
    function* sagaWithBoundActions(action, ...args) {
        const id = getIdFunc(action, defaultId);
        const mdContext = getContext(action);
        if (!id) {
            console.error(
                `action of type ${action.type} has neither id nor form`,
                action
            );
        }
        const sagaResult = yield* saga(
            {
                ...action,
                actions: bindActionCreators(id, actions, {}, { [isSagaAction]: true })()(actionObj => put(actionObj), { mdContext })
            },
            args
        );
        return sagaResult;
    };

/** 
 * Injects api methods into saga args with mdContext values
 * Note: api functions can be called directly when coming from this factory, rather that needing to use `call(fn)`
 */
export const injectApiWithContextFactory = (apiFunctions, decorator = R.identity) => saga =>
    function* sagaWithBoundActions(action, ...args) {
        const { meta: { mdContext } = {} } = action || {};
        const sagaResult = yield* saga(
            {
                ...action,
                api: Object.fromEntries(
                    Object.entries(apiFunctions).map(([ key, fn ]) => [
                        key,
                        decorator(
                            (fnArgs, ...otherArgs) => call(fn, { ...fnArgs, mdContext }, ...otherArgs),
                            mdContext,
                            action
                        ),
                    ])
                ),
                mdContext
            },
            args
        );
        return sagaResult;
    };

// used to omit id in selectors, casuing the underlying logic to use the default id defined for that scope
export const USE_DEFAULT_ID = undefined;

export const batchKeyByActionAndScope = (action, defaultId = "global") => {
    const { meta: { scopeID = defaultId } = { scopeID: defaultId }, type } = action || {};
    return `${type}-${scopeID}`;
};
export const batchPromises = (getBatchKeyFn = batchKeyByActionAndScope, defaultBatchKey = "global") => {
    const proms = {};
    return saga =>
        function* sagaWithBatchedPromiseResolvers(action, ...args) {
            const batchKey = getBatchKeyFn(action, defaultBatchKey);
            const { res, rej, resolve, reject } = action;
            const yes = res || resolve;
            const no = rej || reject;
            const { res: pendingResolves, rej: pendingReject } = proms[batchKey] = proms[batchKey] || { res: new Set(), rej: new Set() };
            const remove = () => {
                pendingResolves.delete(yes);
                pendingResolves.delete(no);
                pendingResolves.delete(remove);
            };
            pendingResolves.add(remove).add(yes);
            pendingReject.add(remove).add(no);
            const resolveFullSet = data => [...pendingResolves].reduce((p, fn) => fn(data) ?? p, null);
            const rejectFullSet = data => [...pendingReject].reduce((p, fn) => fn(data) ?? p, null);
            const sagaResult = yield* saga(
                {
                    ...action,
                    rej: rejectFullSet,
                    reject: rejectFullSet,
                    res: resolveFullSet,
                    resolve: resolveFullSet,
                },
                ...args
            );
            return sagaResult;
        };
};

export const takeFirstAndLatest = (patternOrChannel, saga, wait = 100, {
    leading = true,
    trailing = true,
    maxWait = ONE_SECOND
} = {}, ...args) => {
    if (typeof wait !== "number" || wait < 0) {
        throw new Error(`takeFirstAndLatest's 'wait' needs to be a number >=0, but found ${wait}`);
    }
    if (!leading && !trailing) {
        // saga would never be triggered
        throw new Error(`either takeFirstAndLatest's 'leading' or 'trailing' (or both) need to be true, but found ${leading} and ${trailing}`);
    }
    // eslint-disable-next-line max-statements
    return fork(function* takeFirstAndLatestSaga() {
        let latestTask;
        let latestAction = null;
        while (true) {
            const firstAction = yield take(patternOrChannel);
            if (leading) {
                latestTask = yield fork(saga, ...args.concat(firstAction));
            }
            // eslint-disable-next-line redux-saga/yield-effects
            let maxWaitDelay = maxWait && delay(maxWait);
            while (true) {
                const waitEffect = // either
                    maxWaitDelay ?
                    // eslint-disable-next-line redux-saga/yield-effects
                        race([
                        // maxWait ms have passed
                        // or wait has passed (there has been no patternOrChannel for wait ms)
                        // wait refreshes/re-news/resets whenever a new patternOrChannel (action) comes in
                        // eslint-disable-next-line redux-saga/yield-effects
                            maxWaitDelay, delay(wait)
                        ]) :
                        // eslint-disable-next-line redux-saga/yield-effects
                        delay(wait);
                const {
                    action,
                    doneandtimer
                } = yield race({
                    action: take(patternOrChannel),
                    doneandtimer: latestTask ?
                        all([
                            // wait until last api-call is done and
                            join(latestTask), waitEffect
                        ]) :
                        waitEffect
                });
                // the previous api-call is done and there either (a) has not been a patternOrChannel for wait ms or (b) maxWait ms have passed
                if (doneandtimer) {
                    if (!latestAction) {
                        // there has been no patternOrChannel, hence, we'll enter initial state and wait for one
                        break;
                    }
                    if (trailing) {
                        latestTask = yield fork(saga, ...args.concat(latestAction));
                    }
                    // eslint-disable-next-line redux-saga/yield-effects
                    maxWaitDelay = maxWait && delay(maxWait);
                    latestAction = null;
                }
                // keep the latest patternOrChannel around
                if (action) {
                    latestAction = action;
                }
            }
        }
    });
};

export const withBatchedPromises = batchPromises();

export const takeLatestWithQueue = (patternOrChannel, saga, ...args) => {
    return fork(function* takeFirstAndLatestSaga() {
        let latestTask;
        while (true) {
            const firstAction = yield take(patternOrChannel);
            latestTask = yield fork(saga, ...args.concat(firstAction));
            let latestWaitingAction = null;
            while (true) {
                const {
                    action,
                    taskDone
                } = yield race({
                    action: take(patternOrChannel),
                    taskDone: join(latestTask)
                });
                // a new action has come in, update the latest to-be-done action
                if (action) {
                    latestWaitingAction = action;
                }
                // the ongoing task is done, we're ready to queue a new one
                if (taskDone && latestWaitingAction) {
                    latestTask = yield fork(saga, ...args.concat(latestWaitingAction));
                    latestWaitingAction = null;
                }
                // the ongoing task is done, we would be ready to queue a new one, but there is none
                // -> go back into firstAction waiting mode
                if (taskDone && !latestWaitingAction) {
                    break;
                }
            }
        }
    });
};

export const takeFirstAndCancelLatest = (patternOrChannel, saga, getScope = batchKeyByActionAndScope, ...args) => {
    return fork(function* takeLatestByScopeSaga() {
        while (true) {
            const firstAction = yield take(patternOrChannel);
            const inProgress = {};
            inProgress[getScope(firstAction)] = yield fork(saga, ...args.concat(firstAction));
            while (true) {
                const {
                    action,
                    done
                } = yield race({
                    action: take(patternOrChannel),
                    done: join(Object.values(inProgress))
                });
                // a new action has come in, update the latest to-be-done action
                if (action) { // a new action came in
                    if (inProgress[getScope(action)]) {
                        const latestTask = yield fork(saga, ...args.concat(action));
                        cancel(latestTask); // if there was an action running for this scope, cancel it
                    }
                    // inProgress[getScope(action)] = yield fork(saga, ...args.concat(action)); // start a new saga for this scope
                }
                // all tasks are done, no tasks are running
                if (done) {
                    break;
                }
            }
        }
    });
};

export const takeLatestByScope = (patternOrChannel, saga, getScope = batchKeyByActionAndScope, ...args) => {
    return fork(function* takeLatestByScopeSaga() {
        while (true) {
            const firstAction = yield take(patternOrChannel);
            const inProgress = {};
            inProgress[getScope(firstAction)] = yield fork(saga, ...args.concat(firstAction));

            while (true) {
                const {
                    action,
                    done
                } = yield race({
                    action: take(patternOrChannel),
                    done: join(Object.values(inProgress))
                });
                // a new action has come in, update the latest to-be-done action
                if (action) { // a new action came in
                    if (inProgress[getScope(action)]) {
                        inProgress[getScope(action)].cancel(); // if there was an action running for this scope, cancel it
                    }
                    inProgress[getScope(action)] = yield fork(saga, ...args.concat(action)); // start a new saga for this scope
                }
                // all tasks are done, no tasks are running
                if (done) {
                    break;
                }
            }
        }
    });
};

export const takeLatestByScopeWithQueue = (patternOrChannel, saga, getScope = batchKeyByActionAndScope, ...args) => {
    return fork(function* takeFirstAndLatestSaga() {
        const latestTasks = {};
        while (true) {
            const firstAction = yield take(patternOrChannel);
            const scope = getScope(firstAction);
            latestTasks[scope] = yield fork(saga, ...args.concat(firstAction));
            const latestWaitingActions = {};
            while (true) {
                const {
                    action,
                    taskDone
                } = yield race({
                    action: take(patternOrChannel),
                    taskDone: join(latestTasks[scope])
                });
                // a new action has come in, update the latest to-be-done action
                if (action) {
                    latestWaitingActions[scope] = action;
                }
                // the ongoing task is done, we're ready to queue a new one
                if (taskDone && latestWaitingActions[scope]) {
                    latestTasks[scope] = yield fork(saga, ...args.concat(latestWaitingActions[scope]));
                    latestWaitingActions[scope] = null;
                }
                // the ongoing task is done, we would be ready to queue a new one, but there is none
                // -> go back into firstAction waiting mode
                if (taskDone && !latestWaitingActions[scope]) {
                    break;
                }
            }
        }
    });
};

// eslint-disable-next-line no-magic-numbers
export function* makeRefreshableSaga (sagaGenerator, type, delayTime = 15000) {
    if (!type) console.error("withRefreshRequest must be supplied an action type, or it will not be stopped on unmount");
    let actualdelay = delayTime;
    let skip = false;
    while (true){
        const task = yield fork(function* sagaWithDelayGenerator(){
            if(!skip) {
                yield* sagaGenerator();
            }
            yield delay(actualdelay);
        });
        const { stop, pagevisibilitychange } = yield race({
            timeout: join(task),
            pagevisibilitychange: take(PAGEVISIBILITY),
            stop: take(`STOP_${type}`)
        });
        skip = false;
        if (pagevisibilitychange && !pagevisibilitychange?.data?.isPageVisibile) { // page has gone to the background
            actualdelay = delayTime * 10;
            skip = true;
        }
        if (pagevisibilitychange?.data?.isPageVisibile) { // page has gone to the foreground
            actualdelay = delayTime;
        }
        if (stop){
            break;
        }
    }
}

export const withRefreshRequest = (saga, refreshInterval) => {
    return function* withRefreshRequestGenerator(action, ...args) {
        const makeSaga = () => saga(action, ...args);
        yield* makeRefreshableSaga(makeSaga, action.type, (action.refreshInterval || refreshInterval));  
    };
};

